interface ThemeConfig {
  headerFooterColor: string;
  opacity: number;
  headerTextColor: "White" | "Black";
  logoUrl: string | null;
}

const THEME_CONFIG_KEY = "ThemeConfig";
const THEME_LOGO_KEY = "ThemeLogo";
// Shared contract with Foundation.Admin.Web, which writes this after a theme save.
const THEME_LOGO_SOURCE_KEY = "ThemeLogoSource";

const HEADER_BG_VAR = "--navbar-header-bg";
const HEADER_TEXT_VAR = "--navbar-header-text";
const HEADER_ICON_VAR = "--navbar-header-icon";
const FOOTER_BG_VAR = "--navbar-footer-bg";
const FOOTER_TEXT_VAR = "--navbar-footer-text";

const FETCH_TIMEOUT_MS = 5000;
const HEX_PATTERN = /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/;

const TEXT_HEX: Record<ThemeConfig["headerTextColor"], string> = {
  White: "#FFFFFF",
  Black: "#000000",
};

const isThemeConfig = (value: unknown): value is ThemeConfig => {
  const cfg = value as ThemeConfig;
  return (
    !!cfg &&
    typeof cfg === "object" &&
    typeof cfg.headerFooterColor === "string" &&
    HEX_PATTERN.test(cfg.headerFooterColor) &&
    typeof cfg.opacity === "number" &&
    cfg.opacity >= 0 &&
    cfg.opacity <= 1 &&
    (cfg.headerTextColor === "White" || cfg.headerTextColor === "Black")
  );
};

const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const applyThemeColors = (cfg: ThemeConfig): void => {
  const root = document.documentElement.style;
  const bg = hexToRgba(cfg.headerFooterColor, cfg.opacity);
  const text = TEXT_HEX[cfg.headerTextColor];
  root.setProperty(HEADER_BG_VAR, bg);
  root.setProperty(HEADER_TEXT_VAR, text);
  root.setProperty(HEADER_ICON_VAR, text);
  root.setProperty(FOOTER_BG_VAR, bg);
  root.setProperty(FOOTER_TEXT_VAR, text);
};

const clearThemeColors = (): void => {
  const root = document.documentElement.style;
  [
    HEADER_BG_VAR,
    HEADER_TEXT_VAR,
    HEADER_ICON_VAR,
    FOOTER_BG_VAR,
    FOOTER_TEXT_VAR,
  ].forEach((v) => root.removeProperty(v));
};

const readCachedConfig = (): ThemeConfig | null => {
  const raw = sessionStorage.getItem(THEME_CONFIG_KEY);
  if (!raw) {
    return null;
  }
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Malformed JSON falls through to removal below.
  }
  if (isThemeConfig(parsed)) {
    return parsed;
  }
  sessionStorage.removeItem(THEME_CONFIG_KEY);
  return null;
};

const cacheConfig = (cfg: ThemeConfig): void => {
  const colours: ThemeConfig = {
    headerFooterColor: cfg.headerFooterColor,
    opacity: cfg.opacity,
    headerTextColor: cfg.headerTextColor,
    logoUrl: null,
  };
  try {
    sessionStorage.setItem(THEME_CONFIG_KEY, JSON.stringify(colours));
  } catch {
    // Quota exceeded: the theme is applied, it just isn't cached.
  }
};

const withTimeout = async <T>(
  run: (signal: AbortSignal) => Promise<T>
): Promise<T> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    return await run(controller.signal);
  } finally {
    clearTimeout(timer);
  }
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const fetchLogoAsDataUrl = async (logoUrl: string): Promise<string> => {
  const blob = await withTimeout(async (signal) => {
    const response = await fetch(logoUrl, { signal });
    if (!response.ok) {
      throw new Error(`Logo fetch failed: ${response.status}`);
    }
    return response.blob();
  });
  return blobToDataUrl(blob);
};

const clearCachedLogo = (): void => {
  sessionStorage.removeItem(THEME_LOGO_KEY);
  sessionStorage.removeItem(THEME_LOGO_SOURCE_KEY);
};

const loadTheme = async (
  adminServiceUrl: string,
  csrfToken: string | null
): Promise<void> => {
  const cachedConfig = readCachedConfig();
  if (cachedConfig) {
    applyThemeColors(cachedConfig);
  }

  let status: number;
  let body: unknown;
  try {
    ({ status, body } = await withTimeout(async (signal) => {
      const headers = new Headers();
      headers.append("X-CSRF", csrfToken || "1");
      const response = await fetch(
        `${adminServiceUrl.replace(/\/$/, "")}/api/v1/theme`,
        {
          method: "GET",
          headers,
          redirect: "follow",
          credentials: "include",
          signal,
        }
      );
      return {
        status: response.status,
        body: response.status === 200 ? await response.json() : null,
      };
    }));
  } catch {
    return;
  }

  // Transient failures (401/5xx) keep the cached theme; only 204/404 mean "no theme".
  if (status !== 200 && status !== 204 && status !== 404) {
    return;
  }

  const cfg = isThemeConfig(body) ? body : null;
  if (!cfg) {
    clearThemeColors();
    sessionStorage.removeItem(THEME_CONFIG_KEY);
    clearCachedLogo();
    return;
  }

  applyThemeColors(cfg);
  cacheConfig(cfg);

  if (!cfg.logoUrl) {
    clearCachedLogo();
    return;
  }

  // SAS query strings rotate; the path identifies the uploaded logo.
  const source = cfg.logoUrl.split("?")[0];
  if (
    sessionStorage.getItem(THEME_LOGO_KEY) &&
    sessionStorage.getItem(THEME_LOGO_SOURCE_KEY) === source
  ) {
    return;
  }

  try {
    const dataUrl = await fetchLogoAsDataUrl(cfg.logoUrl);
    sessionStorage.setItem(THEME_LOGO_KEY, dataUrl);
    sessionStorage.setItem(THEME_LOGO_SOURCE_KEY, source);
  } catch {
    clearCachedLogo();
  }
};

export async function bootstrapTheme(
  adminServiceUrl: string,
  csrfToken: string | null
): Promise<void> {
  try {
    await loadTheme(adminServiceUrl, csrfToken);
  } catch {
    // Theming must never block portal startup.
  }
}
